<script>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/no-root-v-if */
// Aceleres — Painel "Sessão expirada" (janela de 24h da API Oficial do WhatsApp).
//
// Aparece no rodapé da conversa (dentro do ReplyBox) quando:
//   - inbox é Channel::Whatsapp, ou Channel::Api marcada pelo gestor como
//     API Oficial (additional_attributes.aceleres_official_api === true); E
//   - currentChat.can_reply === false (janela de 24h expirada); E
//   - não há reinício ativo (o composer estaria destravado).
//
// 3 ações:
//   - Concluir: resolve a conversa (reaproveita action toggleStatus).
//   - Transferir: lista agentes atribuíveis e transfere (action assignAgent).
//   - Reiniciar: reabre + destrava o composer por 24h (endpoint Aceleres).
//     Só visível se o gestor liberou (allow_restart_after_expiry === true).
//
// Antes de Concluir/Transferir/Reiniciar, registra (idempotente) o aviso
// "Sessão expirada" no histórico — deixa de aparecer "do nada" pelo cron.
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import inboxMixin from 'shared/mixins/inboxMixin';
import wootConstants from 'dashboard/constants/globals';
import AceleresSessionAPI from 'dashboard/api/aceleresSession';
import MessageApi from 'dashboard/api/inbox/message';

export default {
  name: 'SessionExpiredPanel',
  mixins: [inboxMixin],
  data() {
    return {
      isLoading: null, // 'concluir' | 'reiniciar' | null
      showAgentList: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      inboxById: 'inboxes/getInbox',
      assignableAgents: 'inboxAssignableAgents/getAssignableAgents',
    }),
    conversationId() {
      return this.currentChat?.id;
    },
    inbox() {
      return this.inboxById(this.currentChat?.inbox_id);
    },
    additionalAttributes() {
      return this.inbox?.additional_attributes || {};
    },
    isOfficialApi() {
      const markedOfficial =
        this.additionalAttributes.aceleres_official_api === true ||
        this.additionalAttributes.aceleres_official_api === 'true';

      return this.isAWhatsAppChannel || (this.isAPIInbox && markedOfficial);
    },
    show() {
      return (
        !!this.conversationId &&
        this.isOfficialApi &&
        this.currentChat?.can_reply === false
      );
    },
    allowRestart() {
      return (
        this.additionalAttributes.allow_restart_after_expiry === true ||
        this.additionalAttributes.allow_restart_after_expiry === 'true'
      );
    },
    currentAssigneeId() {
      return this.currentChat?.meta?.assignee?.id;
    },
    transferableAgents() {
      return (this.assignableAgents || []).filter(
        a => a.id !== this.currentAssigneeId
      );
    },
  },
  watch: {
    conversationId() {
      // Resetar o estado ao trocar de conversa.
      this.showAgentList = false;
      this.isLoading = null;
    },
  },
  methods: {
    async syncRecentMessages() {
      if (!this.conversationId) return;

      try {
        const currentMessages = this.currentChat?.messages || [];
        const lastMessage = currentMessages[currentMessages.length - 1];
        const lastMessageId = lastMessage?.id;
        if (!lastMessageId) return;

        const response = await MessageApi.getPreviousMessages({
          conversationId: this.conversationId,
          after: lastMessageId,
        });
        const payload = response.data?.payload || [];

        const newMessages = payload.filter(message => {
          return !currentMessages.some(existing => existing.id === message.id);
        });

        newMessages.forEach(message => {
          this.$store.dispatch('addMessage', message);
        });

        if (newMessages.length > 0) {
          const lastNewMessage = newMessages[newMessages.length - 1];
          this.$store.dispatch('updateConversationLastActivity', {
            conversationId: this.conversationId,
            lastActivityAt: lastNewMessage.created_at,
          });
        }
      } catch (e) {
        // Best-effort sync; the websocket update still covers the happy path.
      }
    },
    async registerNotice() {
      try {
        await AceleresSessionAPI.registerExpiryNotice(this.conversationId);
      } catch (e) {
        // Não-fatal: o aviso é best-effort. Loga e segue.
        // eslint-disable-next-line no-console
        console.warn('[AceleresSession] aviso de expiração falhou', e);
      }
    },
    async onConcluir() {
      if (this.isLoading) return;
      this.isLoading = 'concluir';
      try {
        await this.registerNotice();
        await this.$store.dispatch('toggleStatus', {
          conversationId: this.conversationId,
          status: wootConstants.STATUS_TYPE.RESOLVED,
        });
        await this.syncRecentMessages();
        useAlert('Atendimento concluído');
      } catch (e) {
        useAlert('Não foi possível concluir o atendimento');
      } finally {
        this.isLoading = null;
      }
    },
    toggleTransferir() {
      this.showAgentList = !this.showAgentList;
    },
    async onTransferTo(agent) {
      if (this.isLoading) return;
      this.isLoading = 'transferir';
      this.showAgentList = false;
      try {
        await this.registerNotice();
        await this.$store.dispatch('setCurrentChatAssignee', {
          conversationId: this.conversationId,
          assignee: agent,
        });
        await this.$store.dispatch('assignAgent', {
          conversationId: this.conversationId,
          agentId: agent.id,
        });
        await this.syncRecentMessages();
        useAlert(`Atendimento transferido para ${agent.name}`);
      } catch (e) {
        useAlert('Não foi possível transferir o atendimento');
      } finally {
        this.isLoading = null;
      }
    },
    async onReiniciar() {
      if (this.isLoading) return;
      this.isLoading = 'reiniciar';
      try {
        await AceleresSessionAPI.restartSession(this.conversationId);
        await this.syncRecentMessages();
        useAlert('Sessão reiniciada — você tem 24h para responder');
      } catch (e) {
        const msg =
          e?.response?.data?.error ||
          'Reabertura não liberada para esta instância';
        useAlert(msg);
      } finally {
        this.isLoading = null;
      }
    },
  },
};
</script>

<template>
  <div v-if="show" class="acl-session-expired">
    <div class="acl-session-expired__top">
      <div class="acl-session-expired__icon" aria-hidden="true">
        <span class="i-lucide-clock-fading" />
      </div>
      <div class="acl-session-expired__text">
        <strong>Atendimento expirado</strong>
        <!-- prettier-ignore -->
        <span>A janela de 24 horas da API Oficial foi encerrada. Escolha uma ação:</span>
      </div>
    </div>

    <div class="acl-session-expired__actions">
      <button
        type="button"
        class="acl-sep-btn acl-sep-btn--primary"
        :disabled="!!isLoading"
        @click="onConcluir"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        Concluir
      </button>

      <button
        type="button"
        class="acl-sep-btn"
        :class="{ 'acl-sep-btn--active': showAgentList }"
        :disabled="!!isLoading"
        @click="toggleTransferir"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="17 1 21 5 17 9" />
          <path d="M3 11V9a4 4 0 0 1 4-4h14" />
          <polyline points="7 23 3 19 7 15" />
          <path d="M21 13v2a4 4 0 0 1-4 4H3" />
        </svg>
        Transferir
      </button>

      <button
        v-if="allowRestart"
        type="button"
        class="acl-sep-btn acl-sep-btn--restart"
        :disabled="!!isLoading"
        @click="onReiniciar"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="23 4 23 10 17 10" />
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
        </svg>
        Reiniciar
      </button>

      <span
        v-else
        class="acl-sep-btn acl-sep-btn--disabled"
        :title="
          allowRestart
            ? ''
            : 'O gestor não liberou a reabertura nesta instância'
        "
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="23 4 23 10 17 10" />
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
        </svg>
        Reiniciar
        <svg
          class="acl-sep-lock"
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </span>
    </div>

    <div v-if="showAgentList" class="acl-session-expired__agents">
      <div
        v-if="!transferableAgents.length"
        class="acl-session-expired__agents-empty"
      >
        Nenhum outro agente disponível nesta inbox.
      </div>
      <button
        v-for="agent in transferableAgents"
        :key="agent.id"
        type="button"
        class="acl-agent-row"
        @click="onTransferTo(agent)"
      >
        <span class="acl-agent-row__avatar">{{
          (agent.name || '?').trim().charAt(0).toUpperCase()
        }}</span>
        <span class="acl-agent-row__name">{{ agent.name }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.acl-session-expired {
  box-sizing: border-box;
  margin: 0.5rem;
  min-height: 0;
  flex: 0 0 auto;
  align-self: stretch;
  padding: 0.75rem 0.875rem;
  border: 1px solid #f1ddca;
  border-radius: 0.75rem;
  background: linear-gradient(135deg, #fffdfa 0%, #fff8ef 100%);
  box-shadow: 0 0.75rem 2rem rgba(104, 74, 45, 0.08);
  color: #454545;
}
.acl-session-expired__top {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.acl-session-expired__icon {
  display: flex;
  width: 2.5rem;
  height: 2.5rem;
  flex: 0 0 2.5rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: #ffeddc;
  color: #c45b16;
}
.acl-session-expired__text {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
  color: #68707a;
}
.acl-session-expired__text strong {
  display: block;
  color: #68707a;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1rem;
}
.acl-session-expired__text span {
  margin: 0.425rem 0 0;
  color: #68707a;
  font-size: 13px;
  line-height: 1.125rem;
}
.acl-session-expired__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 0.75rem;
}
.acl-sep-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 8px;
  border: 1px solid #fdba74;
  background: #fff;
  color: #9a3412;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.12s ease,
    border-color 0.12s ease;
}
.acl-sep-btn:hover:not(:disabled) {
  background: #ffedd5;
}
.acl-sep-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.acl-sep-btn--primary {
  background: #16a34a;
  border-color: #16a34a;
  color: #fff;
}
.acl-sep-btn--primary:hover:not(:disabled) {
  background: #15803d;
}
.acl-sep-btn--restart {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}
.acl-sep-btn--restart:hover:not(:disabled) {
  background: #1d4ed8;
}
.acl-sep-btn--active {
  background: #ffedd5;
  border-color: #fb923c;
}
.acl-sep-btn--disabled {
  background: #f5f5f4;
  border-color: #e7e5e4;
  color: #a8a29e;
  cursor: not-allowed;
  opacity: 1;
}
.acl-sep-lock {
  opacity: 0.8;
}
.acl-session-expired__agents {
  margin-top: 10px;
  max-height: 220px;
  overflow-y: auto;
  border-top: 1px solid #fed7aa;
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.acl-session-expired__agents-empty {
  font-size: 12px;
  color: #a8a29e;
  padding: 6px 2px;
}
.acl-agent-row {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  text-align: left;
  padding: 6px 8px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #1f2937;
  font-size: 13px;
}
.acl-agent-row:hover {
  background: #ffedd5;
}
.acl-agent-row__avatar {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #fb923c;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}
</style>
